import React, { useCallback } from "react";
import { Loading, ospThemes, VirtualGallery } from "@ospweb/sdk-ui";
import { DocsCard } from "./DocsCard";
import Translations from "@translate/components/AIInsight/AIInsightDocsGallery";
import { I18nUtil } from "@utils/i18n";
import { Content } from "@/src/features/api/openData";

const I18n = I18nUtil.createScoped(Translations);

// NewsGallery组件属性
interface AIInsightDocsGalleryProps {
  /** 新闻数据 */

  data: Content[];

  category: string;
  locale: string;
  /** 项目宽度 */
  itemWidth?: number;
  /** 项目高度 */
  itemHeight?: number;
  /** 列间距 */
  columnGap?: number;
  /** 行间距 */
  rowGap?: number;
  /** 容器内边距 */
  padding?: number;
  /** 预加载项目数量，用于优化快速滚动性能 */
  preloadItemCount?: number;
  /** 是否启用无限滚动 */
  enableInfiniteScroll?: boolean;
  // /** 加载更多回调 */
  // onLoadMore?: () => void | Promise<void>;
  // /** 是否正在加载更多 */
  // loadingMore?: boolean;
  // /** 点击新闻项目的回调 */
  // onNewsClick?: (news: NewsItem) => void;
  /** 容器类名 */
  className?: string;
  /** 容器样式 */
  style?: React.CSSProperties;
  deviceType: "mobile" | "desktop";
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  /** SSR 首屏静态渲染数量，Hydrate 后切回 VirtualGallery */
  ssrStaticCount?: number;
  /** 桌面端静态 grid 列数（与固定列布局一致） */
  desktopGridColumns?: number;
}

export const AIInsightDocsGallery: React.FC<AIInsightDocsGalleryProps> = ({
  data,
  itemWidth = 280,
  itemHeight = 240,
  columnGap = 16,
  rowGap = 16,
  padding = 16,
  preloadItemCount = 15,
  enableInfiniteScroll = false,
  className,
  style,
  category,
  locale,
  deviceType,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
}) => {
  // 移除 SSR/CSR 分叉，始终渲染 VirtualGallery，保证结构一致

  const handleLoadMore = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  // 渲染新闻项目

  const renderNewsItem = useCallback(
    (item: any, _index: number) => {
      return (
        <DocsCard
          locale={locale}
          category={category}
          key={item.id}
          news={item}
          deviceType={deviceType}
        />
      );
    },
    [locale, category, deviceType],
  );

  // 没有更多数据内容
  const noMoreContent = (
    <div className="body_m text-content_quaternary mt-12">
      {I18n.t("no more news")}
    </div>
  );

  return (
    <>
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loading fill={ospThemes.colors.content_tertiary} />
        </div>
      ) : data.length > 0 ? (
        <VirtualGallery
          data={data}
          renderItem={renderNewsItem}
          itemWidth={itemWidth}
          itemHeight={itemHeight}
          columnGap={columnGap}
          rowGap={rowGap}
          padding={padding}
          preloadItemCount={preloadItemCount}
          enableInfiniteScroll={enableInfiniteScroll}
          onLoadMore={handleLoadMore}
          loadingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          noMoreContent={noMoreContent}
          className={className}
          style={style}
        />
      ) : (
        <div className="w-full h-full overflow-y-auto no-scrollbar flex items-center justify-center">
          <span className="body_m text-content_quaternary">
            {I18n.t("no news")}
          </span>
        </div>
      )}
    </>
  );
};
